import { useState, useMemo, useEffect } from 'react';
import {
  Plus,
  Copy,
  MinusCircle,
  Clock,
  Info,
  Search,
  X,
  Eye,
  Trash2,
  Power,
  FileText,
  CheckCircle,
  AlertCircle,
  ChevronRight
} from 'lucide-react';
import MultiSelectBase from '../components/MultiSelectBase';

// 需求状态常量
const DEMAND_STATUS = {
  DRAFT: '草稿',
  PENDING: '待审核',
  PUBLISHED: '已发布',
  EXPIRED: '已失效',
};

// 状态颜色配置
const STATUS_COLORS = {
  [DEMAND_STATUS.DRAFT]: { bg: 'bg-slate-50', text: 'text-slate-500', dot: 'bg-slate-400' },
  [DEMAND_STATUS.PENDING]: { bg: 'bg-amber-50', text: 'text-amber-600', dot: 'bg-amber-500' },
  [DEMAND_STATUS.PUBLISHED]: { bg: 'bg-green-50', text: 'text-green-600', dot: 'bg-green-500' },
  [DEMAND_STATUS.EXPIRED]: { bg: 'bg-red-50', text: 'text-red-500', dot: 'bg-red-400' },
};

// 默认表单状态
const DEFAULT_FORM_STATE = {
  demandType: 'open',
  selectedBases: [],
  demandItems: [{ id: 1, material: '', price: '', qty: '', qtyUnlimited: false, deliveryFrom: '', deliveryTo: '' }],
  validFrom: '',
  validTo: '',
  validToUnlimited: true,
  deliveryValidMonths: 6,
  targetSuppliers: [],
};

// 统计卡片组件
const StatCard = ({ title, value, icon: Icon, iconColor, bgColor }) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-slate-500">{title}</p>
        <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
      </div>
      <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center`}>
        <Icon size={24} className={iconColor} />
      </div>
    </div>
  </div>
);

// 状态徽章组件
const StatusBadge = ({ status }) => {
  const colors = STATUS_COLORS[status] || STATUS_COLORS[DEMAND_STATUS.PUBLISHED];
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${colors.bg} ${colors.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
      {status}
    </span>
  );
};

const DemandView = ({ demands, materials, onCreateDemand, onUpdateDemand, initialFilter, setInitialFilter }) => {
  // 弹窗和视图状态
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDemand, setSelectedDemand] = useState(null);

  // 筛选状态
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');

  // 处理从首页传递的初始筛选条件
  useEffect(() => {
    if (initialFilter) {
      if (initialFilter.status && initialFilter.status !== 'all') {
        setFilterStatus(initialFilter.status);
      }
      setInitialFilter?.(null);
    }
  }, [initialFilter, setInitialFilter]);

  // 表单状态
  const [formData, setFormData] = useState(DEFAULT_FORM_STATE);
  const [formErrors, setFormErrors] = useState({});

  const { demandType, selectedBases, demandItems, validFrom, validTo, validToUnlimited, deliveryValidMonths, targetSuppliers } = formData;

  // 根据选择的基地筛选可用物料
  const availableMaterials = useMemo(() => {
    return materials.filter(m =>
      selectedBases.length > 0 && m.baseIds?.some(bid => selectedBases.includes(bid))
    );
  }, [materials, selectedBases]);

  // 计算统计数据
  const stats = useMemo(() => {
    return {
      total: demands.length,
      draft: demands.filter(d => d.status === DEMAND_STATUS.DRAFT).length,
      pending: demands.filter(d => d.status === DEMAND_STATUS.PENDING).length,
      published: demands.filter(d => d.status === DEMAND_STATUS.PUBLISHED).length,
      expired: demands.filter(d => d.status === DEMAND_STATUS.EXPIRED).length,
    };
  }, [demands]);

  // 是否有活跃筛选
  const hasActiveFilters = searchQuery || filterStatus || filterType;

  // 清除筛选
  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
    setFilterType('');
  };

  // 筛选后的需求列表
  const filteredDemands = useMemo(() => {
    let result = [...demands];

    // 搜索筛选
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(d =>
        d.id.toLowerCase().includes(query) ||
        d.material?.toLowerCase().includes(query) ||
        d.creator?.toLowerCase().includes(query)
      );
    }

    // 状态筛选
    if (filterStatus) {
      result = result.filter(d => d.status === filterStatus);
    }

    // 类型筛选
    if (filterType) {
      result = result.filter(d => d.type === filterType);
    }

    return result;
  }, [demands, searchQuery, filterStatus, filterType]);

  // 重置表单
  const resetForm = () => {
    setFormData(DEFAULT_FORM_STATE);
    setFormErrors({});
  };

  const handleOpenModal = () => {
    resetForm();
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const addDemandItem = () => {
    updateFormData('demandItems', [
      ...demandItems,
      { id: Date.now(), material: '', price: '', qty: '', qtyUnlimited: false, deliveryFrom: '', deliveryTo: '' }
    ]);
  };

  const removeDemandItem = (id) => {
    if (demandItems.length > 1) {
      updateFormData('demandItems', demandItems.filter(item => item.id !== id));
    }
  };

  const duplicateDemandItem = (item) => {
    const newItem = {
      ...item,
      id: Date.now(),
      material: '',
    };
    updateFormData('demandItems', [...demandItems, newItem]);
  };

  const updateDemandItem = (id, field, value) => {
    updateFormData('demandItems',
      demandItems.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const validateForm = () => {
    const errors = {};

    if (selectedBases.length === 0) {
      errors.selectedBases = '请选择至少一个收货基地';
    }

    const validItems = demandItems.filter(item =>
      item.material && item.price && (item.qty || item.qtyUnlimited)
    );
    if (validItems.length === 0) {
      errors.demandItems = '请至少填写一条完整的物料明细';
    }

    demandItems.forEach((item, index) => {
      if (item.material && (!item.price || (!item.qty && !item.qtyUnlimited))) {
        errors.demandItems = `第${index + 1}条物料信息不完整`;
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCopy = (demand) => {
    let items = [];

    if (demand.materials && demand.materials.length > 0) {
      items = demand.materials.map((mat, idx) => ({
        id: Date.now() + idx,
        material: mat.name,
        price: mat.price?.toString() || '',
        qty: mat.qtyUnlimited ? '' : (mat.qty?.toString() || ''),
        qtyUnlimited: mat.qtyUnlimited || false,
        deliveryFrom: mat.deliveryFrom || '',
        deliveryTo: mat.deliveryTo || '',
      }));
    } else {
      const materialNames = demand.material?.split(', ') || [];
      const priceMatch = demand.price?.match(/[\d,]+/);
      const price = priceMatch ? priceMatch[0].replace(',', '') : '';
      const qtyMatch = demand.qty?.match(/[\d,]+/);
      const qty = qtyMatch ? qtyMatch[0].replace(',', '') : '';

      items = materialNames.map((name, idx) => ({
        id: Date.now() + idx,
        material: name,
        price: price,
        qty: qty,
        qtyUnlimited: demand.hasUnlimitedQty || false,
        deliveryFrom: '',
        deliveryTo: '',
      }));
    }

    setFormData({
      demandType: demand.type?.includes('闭口') ? 'closed' : 'open',
      selectedBases: demand.baseIds || [],
      demandItems: items,
      validFrom: demand.validFrom || '',
      validTo: demand.validTo || '',
      validToUnlimited: demand.validToUnlimited !== false,
      deliveryValidMonths: demand.deliveryValidMonths || 6,
      targetSuppliers: demand.targetSuppliers || [],
    });
    setShowModal(true);
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const validItems = demandItems.filter(item =>
      item.material && item.price && (item.qty || item.qtyUnlimited)
    );

    const totalQty = validItems.reduce((acc, curr) => {
      if (curr.qtyUnlimited) return acc;
      return acc + Number(curr.qty || 0);
    }, 0);
    const hasUnlimited = validItems.some(item => item.qtyUnlimited);

    const avgPrice = validItems.length > 0
      ? (validItems.reduce((acc, curr) => acc + Number(curr.price || 0), 0) / validItems.length).toFixed(0)
      : 0;

    const newDemand = {
      id: `D${new Date().toISOString().slice(0, 10).replace(/-/g, '')}${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
      type: demandType === 'open' ? '敞口需求' : '定制化(闭口)',
      demandType: demandType,
      baseIds: selectedBases,
      material: validItems.map(i => i.material).join(', '),
      materials: validItems.map(i => ({
        name: i.material,
        price: Number(i.price),
        qty: i.qtyUnlimited ? null : Number(i.qty),
        qtyUnlimited: i.qtyUnlimited || false,
        deliveryFrom: i.deliveryFrom || '',
        deliveryTo: i.deliveryTo || '',
      })),
      price: `${Number(avgPrice).toLocaleString()} 元/吨`,
      qty: hasUnlimited
        ? (totalQty > 0 ? `${totalQty.toLocaleString()}吨+不限` : '不限')
        : `${totalQty.toLocaleString()} 吨`,
      totalQty: totalQty,
      hasUnlimitedQty: hasUnlimited,
      validFrom: validFrom || new Date().toISOString().slice(0, 10),
      validTo: validToUnlimited ? '' : (validTo || getDefaultValidTo()),
      validToUnlimited: validToUnlimited,
      validUntil: validToUnlimited ? '长期有效' : (validTo || getDefaultValidTo()),
      deliveryValidMonths: deliveryValidMonths,
      status: DEMAND_STATUS.PENDING,
      creator: '当前用户',
      createTime: new Date().toLocaleString(),
      targetSuppliers: demandType === 'closed' ? targetSuppliers : [],
    };

    onCreateDemand(newDemand);
    handleCloseModal();
  };

  const getDefaultValidTo = () => {
    const date = new Date();
    date.setMonth(date.getMonth() + 6);
    return date.toISOString().slice(0, 10);
  };

  const handleStatusChange = (demand, newStatus) => {
    if (onUpdateDemand) {
      onUpdateDemand(demand.id, { status: newStatus });
    }
  };

  const handleViewDetail = (demand) => {
    setSelectedDemand(demand);
    setShowDetailModal(true);
  };

  // 渲染物料明细
  const MaterialsDetailCell = ({ demand }) => {
    if (!demand) return <span className="text-slate-400">-</span>;

    const materialsList = demand.materials || [];

    if (materialsList.length === 0 && demand.material) {
      const names = demand.material.split(', ').filter(Boolean);
      if (names.length === 1) {
        return (
          <div className="space-y-1">
            <div className="font-medium text-slate-700 text-sm">{names[0]}</div>
            <div className="text-xs text-slate-500">
              {demand.price} · {demand.qty}
            </div>
          </div>
        );
      }
      return (
        <div className="flex flex-wrap gap-1">
          {names.map((name, idx) => (
            <span key={idx} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
              {name}
            </span>
          ))}
        </div>
      );
    }

    const formatQty = (mat) => {
      if (mat.qtyUnlimited) return '不限';
      return `${mat.qty?.toLocaleString() || 0}吨`;
    };

    if (materialsList.length === 1) {
      const mat = materialsList[0];
      return (
        <div className="space-y-1">
          <div className="font-medium text-slate-700 text-sm">{mat.name}</div>
          <div className="text-xs text-slate-500">
            ¥{mat.price?.toLocaleString()}/吨 · {formatQty(mat)}
          </div>
        </div>
      );
    }

    const displayList = materialsList.slice(0, 3);
    const remaining = materialsList.length - 3;

    return (
      <div className="space-y-1.5">
        {displayList.map((mat, idx) => (
          <div key={idx} className="flex items-center gap-2 text-xs">
            <span className="px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded font-medium">
              {mat.name}
            </span>
            <span className="text-slate-500">
              ¥{mat.price?.toLocaleString()}/吨 · {formatQty(mat)}
            </span>
          </div>
        ))}
        {remaining > 0 && (
          <div className="text-xs text-blue-500 cursor-pointer hover:underline">
            +{remaining}项更多
          </div>
        )}
      </div>
    );
  };

  // 渲染新建/编辑弹窗
  const renderModal = () => {
    if (!showModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl w-[700px] p-0 overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col max-h-[90vh]">
          <div className="p-5 border-b border-slate-100 flex justify-between items-center">
            <h3 className="text-lg font-bold text-slate-800">发布采购需求</h3>
            <button onClick={handleCloseModal} className="text-slate-400 hover:text-slate-600">
              <X size={20} />
            </button>
          </div>

          <div className="p-5 space-y-5 overflow-y-auto flex-1">
            {/* 需求类型切换 */}
            <div className="flex gap-4 bg-slate-100 p-1 rounded-lg">
              <button
                onClick={() => updateFormData('demandType', 'open')}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                  demandType === 'open' ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                敞口需求 (Open)
              </button>
              <button
                onClick={() => updateFormData('demandType', 'closed')}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                  demandType === 'closed' ? 'bg-white shadow text-purple-600' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                定制化/闭口需求 (Targeted)
              </button>
            </div>

            {/* 基地选择 */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">
                收货基地 (支持多选) <span className="text-red-500">*</span>
              </label>
              <MultiSelectBase
                selectedIds={selectedBases}
                onChange={(ids) => updateFormData('selectedBases', ids)}
                placeholder="请选择收货基地 (可多选)..."
              />
              {formErrors.selectedBases && (
                <p className="text-xs text-red-500 mt-1">{formErrors.selectedBases}</p>
              )}
            </div>

            {/* 物料明细 */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="block text-xs font-bold text-slate-700">
                  需求物料明细 (可添加多行)
                </label>
                <button
                  onClick={addDemandItem}
                  className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-800"
                >
                  <Plus size={12} /> 添加物料
                </button>
              </div>

              {formErrors.demandItems && (
                <p className="text-xs text-red-500">{formErrors.demandItems}</p>
              )}

              {demandItems.map((item) => (
                <div
                  key={item.id}
                  className="p-4 border border-slate-200 rounded-lg bg-slate-50 relative group"
                >
                  <div className="absolute -top-2 -right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => duplicateDemandItem(item)}
                      className="bg-blue-100 text-blue-500 rounded-full p-1 shadow-sm hover:bg-blue-200"
                      title="复制此物料行"
                    >
                      <Copy size={14} />
                    </button>
                    {demandItems.length > 1 && (
                      <button
                        onClick={() => removeDemandItem(item.id)}
                        className="bg-red-100 text-red-500 rounded-full p-1 shadow-sm hover:bg-red-200"
                        title="删除此物料行"
                      >
                        <MinusCircle size={14} />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div className="col-span-2">
                      <label className="block text-[10px] text-slate-400 mb-1">
                        物料名称 {selectedBases.length ? '' : '(请先选择基地)'}
                      </label>
                      <select
                        className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        value={item.material}
                        onChange={(e) => updateDemandItem(item.id, 'material', e.target.value)}
                        disabled={!selectedBases.length}
                      >
                        <option value="">请选择物料...</option>
                        {availableMaterials.map(m => (
                          <option key={m.id} value={m.name}>{m.name} ({m.unit})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">
                        {demandType === 'open' ? '最高单价 (元/吨)' : '固定收购价 (元/吨)'}
                      </label>
                      <input
                        type="number"
                        className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        value={item.price}
                        onChange={(e) => updateDemandItem(item.id, 'price', e.target.value)}
                        placeholder="输入单价"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">
                        {demandType === 'open' ? '预估收购量 (吨)' : '固定收购量 (吨)'}
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 disabled:bg-slate-100 disabled:text-slate-400"
                          value={item.qty}
                          onChange={(e) => updateDemandItem(item.id, 'qty', e.target.value)}
                          placeholder={item.qtyUnlimited ? '不限' : '输入数量'}
                          disabled={item.qtyUnlimited}
                        />
                        {demandType === 'open' && (
                          <label className={`flex items-center gap-1.5 text-xs whitespace-nowrap cursor-pointer px-2 py-1 rounded-md transition-colors ${
                            item.qtyUnlimited
                              ? 'bg-blue-100 text-blue-700 font-medium'
                              : 'text-slate-500 hover:bg-slate-100'
                          }`}>
                            <input
                              type="checkbox"
                              checked={item.qtyUnlimited || false}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                updateFormData('demandItems',
                                  demandItems.map(i =>
                                    i.id === item.id
                                      ? { ...i, qtyUnlimited: isChecked, qty: isChecked ? '' : i.qty }
                                      : i
                                  )
                                );
                              }}
                              className="w-3.5 h-3.5 text-blue-600 rounded accent-blue-600"
                            />
                            不限
                          </label>
                        )}
                      </div>
                    </div>
                  </div>

                  {demandType === 'closed' && (
                    <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[10px] text-slate-400 mb-1">价格有效期 (起始日期)</label>
                          <input
                            type="date"
                            className="w-full border border-purple-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                            value={validFrom}
                            onChange={(e) => updateFormData('validFrom', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-slate-400 mb-1">价格有效期 (结束日期)</label>
                          <input
                            type="date"
                            className="w-full border border-purple-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                            value={validTo}
                            onChange={(e) => updateFormData('validTo', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[10px] text-slate-400 mb-1">收货有效期 (起始日期)</label>
                          <input
                            type="date"
                            className="w-full border border-purple-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                            value={item.deliveryFrom || ''}
                            onChange={(e) => updateDemandItem(item.id, 'deliveryFrom', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-slate-400 mb-1">收货有效期 (结束日期)</label>
                          <input
                            type="date"
                            className="w-full border border-purple-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                            value={item.deliveryTo || ''}
                            onChange={(e) => updateDemandItem(item.id, 'deliveryTo', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* 敞口需求有效期 */}
            {demandType === 'open' && (
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-100 space-y-4">
                <h4 className="text-xs font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <Clock size={14} /> 有效期设置
                </h4>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1.5">价格有效期（起始日期）</label>
                    <input
                      type="date"
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      value={validFrom}
                      onChange={(e) => updateFormData('validFrom', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1.5">价格有效期（结束日期）</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-100"
                        value={validTo}
                        onChange={(e) => updateFormData('validTo', e.target.value)}
                        disabled={validToUnlimited}
                        placeholder={validToUnlimited ? '长期有效' : ''}
                      />
                      <label className={`flex items-center gap-1.5 text-xs whitespace-nowrap cursor-pointer px-2 py-1.5 rounded-md transition-colors ${
                        validToUnlimited
                          ? 'bg-blue-100 text-blue-700 font-medium'
                          : 'text-slate-500 hover:bg-slate-100'
                      }`}>
                        <input
                          type="checkbox"
                          checked={validToUnlimited || false}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setFormData(prev => ({
                              ...prev,
                              validToUnlimited: isChecked,
                              validTo: isChecked ? '' : prev.validTo
                            }));
                          }}
                          className="w-3.5 h-3.5 text-blue-600 rounded accent-blue-600"
                        />
                        长期
                      </label>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Info size={14} className="text-blue-500" />
                      <span className="text-xs text-slate-600">
                        <b>收货有效期：</b>用户响应需求后 <b className="text-blue-600">{deliveryValidMonths} 个月</b> 内
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="px-2 py-1 text-xs bg-white border border-slate-200 rounded hover:bg-slate-50"
                        onClick={() => updateFormData('deliveryValidMonths', Math.max(1, deliveryValidMonths - 1))}
                      >
                        -
                      </button>
                      <span className="px-3 py-1 text-xs font-medium text-slate-700">{deliveryValidMonths}</span>
                      <button
                        type="button"
                        className="px-2 py-1 text-xs bg-white border border-slate-200 rounded hover:bg-slate-50"
                        onClick={() => updateFormData('deliveryValidMonths', Math.min(24, deliveryValidMonths + 1))}
                      >
                        +
                      </button>
                      <span className="text-xs text-slate-500 ml-1">个月</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 闭口需求定向设置 */}
            {demandType === 'closed' && (
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                <label className="block text-xs font-bold text-purple-700 mb-2">定向设置 (仅特定客户可见)</label>
                <input
                  type="text"
                  placeholder="输入客户姓名或身份证搜索..."
                  className="w-full border border-purple-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
                <div className="mt-2 flex gap-2">
                  {targetSuppliers.map((supplier, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-white rounded border border-purple-200 text-xs text-purple-600 flex items-center gap-1"
                    >
                      {supplier}
                      <button
                        className="hover:text-red-500"
                        onClick={() => updateFormData('targetSuppliers', targetSuppliers.filter((_, i) => i !== idx))}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-slate-100 flex justify-end gap-3 bg-slate-50">
            <button
              onClick={handleCloseModal}
              className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-100 rounded-lg"
            >
              取消
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
            >
              提交审核
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 渲染详情弹窗
  const renderDetailModal = () => {
    if (!showDetailModal || !selectedDemand) return null;

    // 获取物料列表
    const getMaterialsList = () => {
      if (selectedDemand.materials && selectedDemand.materials.length > 0) {
        return selectedDemand.materials;
      }
      if (selectedDemand.material) {
        const names = selectedDemand.material.split(', ').filter(Boolean);
        if (names.length === 1) {
          return [{
            name: names[0],
            price: selectedDemand.price?.match(/[\d,]+/)?.[0]?.replace(',', '') || 0,
            qty: selectedDemand.totalQty || selectedDemand.qty?.match(/[\d,]+/)?.[0]?.replace(',', '') || 0,
            qtyUnlimited: selectedDemand.hasUnlimitedQty || false,
          }];
        }
        return names.map(name => ({ name, price: '-', qty: '-' }));
      }
      return [];
    };

    const materialsList = getMaterialsList();
    const hasMultipleMaterials = materialsList.length > 1;

    const formatQty = (mat) => {
      if (mat.qtyUnlimited) return '不限';
      if (mat.qty === '-' || mat.qty === undefined) return '-';
      return `${Number(mat.qty).toLocaleString()}吨`;
    };

    const formatPrice = (mat) => {
      if (mat.price === '-' || mat.price === undefined) return '-';
      return `¥${Number(mat.price).toLocaleString()}/吨`;
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl w-[600px] p-0 overflow-hidden animate-in fade-in zoom-in duration-200">
          <div className="p-5 border-b border-slate-100 flex justify-between items-center">
            <h3 className="text-lg font-bold text-slate-800">需求详情</h3>
            <button onClick={() => setShowDetailModal(false)} className="text-slate-400 hover:text-slate-600">
              <X size={20} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400">需求编号</label>
                <p className="font-medium">{selectedDemand.id}</p>
              </div>
              <div>
                <label className="text-xs text-slate-400">状态</label>
                <p><StatusBadge status={selectedDemand.status} /></p>
              </div>
              <div>
                <label className="text-xs text-slate-400">类型</label>
                <p className="font-medium">{selectedDemand.type}</p>
              </div>
              <div>
                <label className="text-xs text-slate-400">有效期至</label>
                <p className="font-medium">{selectedDemand.validUntil}</p>
              </div>
              <div>
                <label className="text-xs text-slate-400">创建人</label>
                <p className="font-medium">{selectedDemand.creator}</p>
              </div>
              <div>
                <label className="text-xs text-slate-400">创建时间</label>
                <p className="font-medium">{selectedDemand.createTime}</p>
              </div>
            </div>

            {/* 物料明细 */}
            <div className="border-t border-slate-100 pt-4">
              <label className="text-xs font-medium text-slate-500 mb-3 block">物料明细</label>
              {materialsList.length > 0 ? (
                <div className="space-y-2">
                  {materialsList.map((mat, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg ${hasMultipleMaterials ? 'bg-slate-50 border border-slate-100' : 'bg-white'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {hasMultipleMaterials && (
                            <span className="w-5 h-5 flex items-center justify-center bg-blue-100 text-blue-600 text-xs rounded-full font-medium">
                              {idx + 1}
                            </span>
                          )}
                          <span className="font-medium text-slate-800">{mat.name}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-blue-600 font-medium">{formatPrice(mat)}</span>
                          <span className="text-slate-300 mx-2">·</span>
                          <span className="text-slate-600">{formatQty(mat)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400">暂无物料信息</p>
              )}
            </div>
          </div>

          <div className="p-4 border-t border-slate-100 flex justify-end gap-3 bg-slate-50">
            <button
              onClick={() => setShowDetailModal(false)}
              className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-100 rounded-lg"
            >
              关闭
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">采购需求管理</h1>
          <p className="text-slate-500 text-sm mt-1">管理所有采购需求和发布</p>
        </div>
        <button
          onClick={handleOpenModal}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm"
        >
          <Plus size={16} /> 发布采购需求
        </button>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard
          title="全部需求"
          value={stats.total}
          icon={FileText}
          iconColor="text-slate-600"
          bgColor="bg-slate-50"
        />
        <StatCard
          title="草稿"
          value={stats.draft}
          icon={FileText}
          iconColor="text-slate-500"
          bgColor="bg-slate-50"
        />
        <StatCard
          title="待审核"
          value={stats.pending}
          icon={Clock}
          iconColor="text-amber-600"
          bgColor="bg-amber-50"
        />
        <StatCard
          title="已发布"
          value={stats.published}
          icon={CheckCircle}
          iconColor="text-green-600"
          bgColor="bg-green-50"
        />
        <StatCard
          title="已失效"
          value={stats.expired}
          icon={AlertCircle}
          iconColor="text-red-500"
          bgColor="bg-red-50"
        />
      </div>

      {/* 筛选区域 */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-wrap gap-3">
            {/* 搜索框 */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="搜索需求编号、物料、创建人..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* 状态筛选 */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="">全部状态</option>
              <option value={DEMAND_STATUS.DRAFT}>草稿</option>
              <option value={DEMAND_STATUS.PENDING}>待审核</option>
              <option value={DEMAND_STATUS.PUBLISHED}>已发布</option>
              <option value={DEMAND_STATUS.EXPIRED}>已失效</option>
            </select>

            {/* 类型筛选 */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="">全部类型</option>
              <option value="敞口需求">敞口需求</option>
              <option value="定制化(闭口)">定制化(闭口)</option>
            </select>

            {/* 清除筛选 */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                <X size={16} />
                清除筛选
              </button>
            )}
          </div>
        </div>

        {/* 表格区域 */}
        {filteredDemands.length === 0 ? (
          <div className="p-12 text-center">
            <FileText size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">暂无需求数据</p>
            {hasActiveFilters && (
              <p className="text-sm text-slate-400 mt-1">尝试调整筛选条件</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">需求编号</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">类型</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">回收基地</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider min-w-[200px]">物料明细</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">有效期</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">状态</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">创建人</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">创建时间</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredDemands.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-3">
                      <span className="font-mono text-xs text-slate-600">{item.id}</span>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs border ${
                        item.type?.includes('闭口')
                          ? 'bg-purple-50 text-purple-700 border-purple-100'
                          : 'bg-blue-50 text-blue-700 border-blue-100'
                      }`}>
                        {item.type}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="text-xs text-slate-600">{item.baseNames || '-'}</span>
                    </td>
                    <td className="p-3">
                      <MaterialsDetailCell demand={item} />
                    </td>
                    <td className="p-3">
                      <span className="text-xs text-slate-600">{item.validUntil}</span>
                    </td>
                    <td className="p-3">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-600">{item.creator}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-xs text-slate-500">{item.createTime}</span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewDetail(item)}
                          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        >
                          <Eye size={14} />
                          详情
                          <ChevronRight size={14} />
                        </button>
                        <button
                          onClick={() => handleCopy(item)}
                          className="text-sm text-slate-500 hover:text-slate-700"
                        >
                          复制
                        </button>
                        {item.status === DEMAND_STATUS.DRAFT && (
                          <>
                            <button className="text-sm text-green-600 hover:text-green-700">提交</button>
                            <button className="text-sm text-red-500 hover:text-red-600 flex items-center gap-1">
                              <Trash2 size={12} /> 删除
                            </button>
                          </>
                        )}
                        {item.status === DEMAND_STATUS.PENDING && (
                          <button
                            onClick={() => handleStatusChange(item, DEMAND_STATUS.DRAFT)}
                            className="text-sm text-amber-500 hover:text-amber-600"
                          >
                            撤回
                          </button>
                        )}
                        {item.status === DEMAND_STATUS.PUBLISHED && (
                          <button
                            onClick={() => handleStatusChange(item, DEMAND_STATUS.EXPIRED)}
                            className="text-sm text-amber-500 hover:text-amber-600 flex items-center gap-1"
                          >
                            <Power size={12} /> 关闭
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 底部统计 */}
        {filteredDemands.length > 0 && (
          <div className="p-4 border-t border-slate-100 bg-slate-50">
            <p className="text-sm text-slate-500">
              共 <span className="font-medium text-slate-700">{filteredDemands.length}</span> 条记录
              {hasActiveFilters && (
                <span className="ml-2">
                  （筛选自 <span className="font-medium text-slate-700">{demands.length}</span> 条）
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* 弹窗 */}
      {renderModal()}
      {renderDetailModal()}
    </div>
  );
};

export default DemandView;
